import React from "react";
import { _handleError } from "./scanViewErrorHandlerHelper";
import { CaptureType } from "../api/cardApiModel";
import {
  generateImageFromCanvas,
  generateImageMetadata,
} from "./scanCanvasImageHelper";
import { isIOS } from "react-device-detect";
import { dataURLtoFile, calculateGuides } from "./helper";
import { Dispatch, SetStateAction } from "react";
import detectorWrapper from "./detector";
import { GlobalLogger } from "./debugLogger";
import {
  getDeviceInfo,
  getEnvironmentInfo,
  getRefSize,
} from "../utils/getInfoFunctions";

//Main scan processing handler for scans -- called every ~250ms when active.
//Handles: auto & manual scan

//---------------------------------------
// Hot Path
//---------------------------------------

export const scanHandler = (
  videoRefBg: React.RefObject<HTMLVideoElement>,
  frameDivRef: React.RefObject<HTMLDivElement>,
  captureCanvasRef: React.RefObject<HTMLCanvasElement>,
  detectionCanvasRef: React.RefObject<HTMLCanvasElement>,
  debugCanvasLatestRef: React.RefObject<HTMLCanvasElement>,
  debugCanvasBestRef: React.RefObject<HTMLCanvasElement>,
  scanCountRef: React.MutableRefObject<number>,
  performanceCountRef: React.MutableRefObject<number>,
  setLatestScore: Dispatch<SetStateAction<number>>,
  setLatestLaplacian: Dispatch<SetStateAction<number>>,
  setBestScore: Dispatch<SetStateAction<number>>,
  setBestLaplacian: Dispatch<SetStateAction<number>>,
  setModelState: Dispatch<SetStateAction<string>>,
  setScanDebugState: Dispatch<SetStateAction<string>>,
  setScanProgress: Dispatch<SetStateAction<number>>,
  flipHorizontal = false,
  flipVertical = false,
  scanCaptureBuffer: any,
  threshold: any,
  laplacianThreshold: any,
  onError: any,
  scanProgress: any,
  softMaxUploads: any,
  bufferSize: any,
  videoMetadata: any,
  cardScanApi: any,
  cardIdRef: any,
  stateMachineState: any,
  stateMachineSend: any,
  debugMenu?: boolean
) => {
  const video = videoRefBg.current;
  if (!video || stateMachineState.matches("cameraLoading")) {
    GlobalLogger.error("scanHandler -- no video found");
    return;
  }
  if (!cardIdRef.current) {
    GlobalLogger.log("scanHandler -- no card Id found");
    return;
  }

  const detectionCanvas = detectionCanvasRef.current;
  const captureCanvas = captureCanvasRef.current;
  const [guideX, guideY, guideWidth, guideHeight] = calculateGuides(
    videoRefBg,
    frameDivRef
  );

  GlobalLogger.debug(
    `Canvas: ${captureCanvas?.width}, ${captureCanvas?.height}`,
    "everything"
  );
  GlobalLogger.debug(
    `Video: ${video.videoWidth}, ${video.videoHeight}`,
    "everything"
  );

  if (!(detectionCanvas && captureCanvas)) {
    GlobalLogger.error("CardScan: no canvas found - critical failure");
    return;
  }

  //resize image to canvas sizes
  detectionCanvas
    .getContext("2d")
    ?.drawImage(
      video,
      guideX,
      guideY,
      guideWidth,
      guideHeight,
      0,
      0,
      detectionCanvas.width,
      detectionCanvas.height
    );

  let jpegUrl;

  //work around for iOS GPU memory issue.
  //TODO: check we still need this and remove if not. might have been fixed by useInterval
  if (!isIOS) {
    jpegUrl = generateImageFromCanvas(
      videoRefBg,
      frameDivRef,
      captureCanvasRef,
      flipHorizontal,
      flipVertical
    );
  }

  detectCard(
    videoRefBg,
    frameDivRef,
    captureCanvasRef,
    detectionCanvas,
    debugCanvasLatestRef,
    debugCanvasBestRef,
    scanCountRef,
    performanceCountRef,
    setLatestScore,
    setLatestLaplacian,
    setBestScore,
    setBestLaplacian,
    setModelState,
    setScanDebugState,
    flipHorizontal,
    flipVertical,
    threshold,
    laplacianThreshold,
    onError,
    scanCaptureBuffer,
    scanProgress,
    setScanProgress,
    softMaxUploads,
    bufferSize,
    videoMetadata,
    cardScanApi,
    cardIdRef,
    stateMachineState,
    stateMachineSend,
    jpegUrl,
    debugMenu
  );
};

const detectCard = (
  videoRefBg: React.RefObject<HTMLVideoElement>,
  frameDivRef: React.RefObject<HTMLDivElement>,
  captureCanvasRef: React.RefObject<HTMLCanvasElement>,
  detectionCanvas: HTMLCanvasElement,
  debugCanvasLatestRef: React.RefObject<HTMLCanvasElement>,
  debugCanvasBestRef: React.RefObject<HTMLCanvasElement>,
  scanCountRef: React.MutableRefObject<number>,
  performanceCountRef: React.MutableRefObject<number>,
  setLatestScore: Dispatch<SetStateAction<number>>,
  setLatestLaplacian: Dispatch<SetStateAction<number>>,
  setBestScore: Dispatch<SetStateAction<number>>,
  setBestLaplacian: Dispatch<SetStateAction<number>>,
  setModelState: Dispatch<SetStateAction<string>>,
  setScanDebugState: Dispatch<SetStateAction<string>>,
  flipHorizontal: boolean,
  flipVertical: boolean,
  threshold: number,
  laplacianThreshold: number,
  onError: any,
  scanCaptureBuffer: any,
  scanProgress: any,
  setScanProgress: any,
  softMaxUploads: any,
  bufferSize: any,
  videoMetadata: any,
  cardScanApi: any,
  cardIdRef: any,
  stateMachineState: any,
  stateMachineSend: any,
  jpegUrl?: string,
  debugMenu?: boolean
) => {
  detectorWrapper
    .get()
    .predict_image(detectionCanvas, flipHorizontal, flipVertical)
    .then((ret) => {
      performanceCountRef.current += 1;

      if (ret === null) {
        GlobalLogger.log("cardDetector race condition -- early exit");
        return;
      }

      // Save the performance data approximately every 2.5 seconds
      if (performanceCountRef.current % 10 === 0 && cardIdRef.current) {
        cardScanApi.current
          .performanceData(cardIdRef.current, ret)
          .catch((error: any) => {
            GlobalLogger.error(error);
          });
      }

      if (debugMenu) {
        setModelState(detectorWrapper.get().modelState);
        setScanDebugState("active");
      }

      if (
        ret["modelClass"] === "card" &&
        ["autoCapture", "frontsideProcessing", "backsideProcessing"].some(
          stateMachineState.matches
        )
      ) {
        const score = Number(ret["modelProbability"]);
        const laplacian = Number(ret["laplaceVariance"]);

        GlobalLogger.debug(`Score: ${score} Lap: ${laplacian}`, "detector");

        if (debugMenu) {
          setLatestScore(score);
          setLatestLaplacian(laplacian);
          renderDebugCanvas(
            videoRefBg,
            frameDivRef,
            debugCanvasLatestRef,
            debugMenu
          );
        }

        // Add quality images to the buffer...
        if (score > threshold && laplacian > laplacianThreshold) {
          GlobalLogger.debug(
            `scores above threshold: ${threshold} lap threshold: ${laplacianThreshold}`,
            "detector"
          );

          if (debugMenu) {
            setBestScore(score);
            setBestLaplacian(laplacian);
            renderDebugCanvas(
              videoRefBg,
              frameDivRef,
              debugCanvasBestRef,
              debugMenu
            );
          }

          //TODO: see if this is still an issue.
          if (jpegUrl === undefined) {
            //lazy jpeg capture to workaround iOS GPU memory issue
            //downside is that we capture the submitted image ~10-15ms after detection but should be Ok
            const jpeg = generateImageFromCanvas(
              videoRefBg,
              frameDivRef,
              captureCanvasRef,
              flipHorizontal,
              flipVertical
            );
            addImageToBuffer(
              scanCountRef,
              jpeg!,
              score,
              laplacian,
              scanCaptureBuffer,
              scanProgress,
              setScanProgress,
              softMaxUploads,
              bufferSize,
              videoRefBg,
              frameDivRef,
              captureCanvasRef,
              threshold,
              laplacianThreshold,
              videoMetadata,
              cardScanApi,
              cardIdRef,
              stateMachineState,
              onError
            );
          } else {
            addImageToBuffer(
              scanCountRef,
              jpegUrl,
              score,
              laplacian,
              scanCaptureBuffer,
              scanProgress,
              setScanProgress,
              softMaxUploads,
              bufferSize,
              videoRefBg,
              frameDivRef,
              captureCanvasRef,
              threshold,
              laplacianThreshold,
              videoMetadata,
              cardScanApi,
              cardIdRef,
              stateMachineState,
              onError
            );
          }
        }
      }
    })
    .catch((error) => {
      _handleError(error, stateMachineSend, onError);
    });
};

const addImageToBuffer = (
  scanCountRef: React.MutableRefObject<number>,
  jpegUrl: string,
  score: number,
  laplacian: number,
  scanCaptureBuffer: any,
  scanProgress: any,
  setScanProgress: any,
  softMaxUploads: any,
  bufferSize: any,
  videoRefBg: React.RefObject<HTMLVideoElement>,
  frameDivRef: React.RefObject<HTMLDivElement>,
  captureCanvasRef: React.RefObject<HTMLCanvasElement>,
  threshold: any,
  laplacianThreshold: any,
  videoMetadata: any,
  cardScanApi: any,
  cardIdRef: any,
  stateMachineState: any,
  onError: any
) => {
  const file = dataURLtoFile(jpegUrl, "card.jpeg");
  scanCaptureBuffer.current.push({ file, score, laplacian });

  //TODO: update progress UI -- a little slow to update, we could use the scanCapture buffer.
  GlobalLogger.debug(
    `Adding image to capture buffer: ${scanProgress} lap: ${laplacian} score: ${score}`
  );

  setScanProgress((p: any) => {
    if (p >= (softMaxUploads * bufferSize) / 2) {
      return p + 0.5;
    } else {
      return p + 1;
    }
  });

  const numScans = Math.min(
    Math.ceil(scanProgress),
    softMaxUploads * bufferSize
  );

  const totalProgress = 20 + (numScans / (softMaxUploads * bufferSize)) * 100;

  document.documentElement.style.setProperty(
    "--cardscan-corner-height",
    `${totalProgress >= 100 ? 100 : totalProgress}%`
  );

  document.documentElement.style.setProperty(
    "--cardscan-corner-width",
    `${totalProgress >= 100 ? 100 : totalProgress}%`
  );

  document.documentElement.style.setProperty(
    "--cardscan-buffer-size",
    numScans.toString()
  );

  if (scanCaptureBuffer.current.length === bufferSize) {
    scanCountRef.current += 1;

    GlobalLogger.debug(
      `capture buffer full: ${scanCaptureBuffer.current.length}`
    );

    //pull best / least blurry image from buffer
    const lapScores = scanCaptureBuffer.current.map((i: any) =>
      Number(i.laplacian)
    );
    const maxScore = Math.max.apply(null, lapScores);
    const index = lapScores.indexOf(maxScore);

    const bestImage = scanCaptureBuffer.current[index].file;

    GlobalLogger.debug(`Best Image picked: ${index} - lap: ${maxScore}`);

    const environmentInfo = getEnvironmentInfo();
    const deviceInfo = getDeviceInfo();

    const videoElementInfo = getRefSize(videoRefBg);
    const guideFrameElementInfo = getRefSize(frameDivRef);

    //Note: metadata might have changed between capture and upload.
    const metadata = generateImageMetadata(
      CaptureType.automatic,
      videoRefBg,
      frameDivRef,
      captureCanvasRef,
      threshold,
      laplacianThreshold,
      videoMetadata,
      environmentInfo,
      videoElementInfo,
      guideFrameElementInfo,
      deviceInfo
    );

    GlobalLogger.debug(JSON.stringify(metadata), "everything");

    const scores = scanCaptureBuffer.current.map((i: any) => ({
      score: i.score,
      laplacian: i.laplacian,
    }));

    metadata["capture_score"] = { scores: scores, max_lapScore: maxScore };

    uploadFile(
      scanCountRef.current,
      bestImage,
      CaptureType.automatic,
      metadata,
      cardScanApi,
      cardIdRef,
      stateMachineState,
      onError,
      () => {
        GlobalLogger.debug("image upload complete");
      }
    );

    //start capture buffer here so that we start the upload timer right away
    scanCaptureBuffer.current = [];
    GlobalLogger.debug("capture buffer cleared");
  }
};

const uploadFile = async (
  scanCount: number,
  file: File,
  captureType: CaptureType,
  metadata: { [key: string]: string },
  cardScanApi: any,
  cardIdRef: any,
  stateMachineState: any,
  stateMachineSend: any,
  onError: any,
  cb?: any
) => {
  cardScanApi.current
    ?.uploadScanImage(
      scanCount,
      file,
      cardIdRef.current,
      captureType,
      ["backsideProcessing", "backsidePending"].some(stateMachineState.matches)
        ? "back"
        : "front",
      metadata
    )
    .then(() => {
      if (cb) {
        cb();
      }
      GlobalLogger.debug("Upload Success");
    })
    .catch((error: any) => {
      _handleError(error, stateMachineSend, onError);
    });
};

//---------------------------------------
// Debug Menu
//---------------------------------------

//debug data to be passed in with every scan
const renderDebugCanvas = (
  videoRefBg: React.RefObject<HTMLVideoElement>,
  frameDivRef: React.RefObject<HTMLDivElement>,
  debugCanvasRef: React.RefObject<HTMLCanvasElement>,
  debugMenu?: boolean
) => {
  const videoBg = videoRefBg.current;
  if (debugMenu && debugCanvasRef && videoBg) {
    const [guideX, guideY, guideWidth, guideHeight] = calculateGuides(
      videoRefBg,
      frameDivRef
    );

    const dCanvas = debugCanvasRef.current;
    if (dCanvas) {
      const destCtx = dCanvas.getContext("2d", { alpha: false });
      destCtx?.drawImage(
        videoBg,
        guideX,
        guideY,
        guideWidth,
        guideHeight,
        0,
        0,
        dCanvas.width,
        dCanvas.height
      );
    }
  }
};
